/**
 * Whether the assistant panel may be shown, and whether it may speak:
 * queries `agentAccess { enabled speech }` and hides both on any refusal
 * (`MissingCapabilities`/`MissingPermissions`) or network failure, per the
 * `ai-assistant-access` and `ai-assistant-chat-panel` specs (MEIS-13516, P1
 * — replaces `GET /api/agent/access`). Re-probed whenever the selected user,
 * the organisation or its `aiAssistant` capability changes.
 *
 * A caller refused by the backend's own gate (a missing `aiAssistant`
 * capability or permission) never sees this as a GraphQL error —
 * the resolver answers `{ enabled: false, speech: false }` rather than
 * throwing (`agentAccess.ts`). `noErrorNotification` on this query's
 * context is defence in depth on top of that: it keeps the global Apollo
 * error link (`ApolloClientProvider.tsx`) from toasting even if this probe
 * ever does surface a GraphQL error — a user without access must never see
 * an error toast right after login, the assistant just stays invisible.
 */
import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import React from "react";
import { useSelectedUser } from "../../auth/useSelectedUser.js";
import { useUserData } from "../../auth/useUserData.js";
import {
  AgentAccessDocument,
  type AgentAccessQuery,
} from "./useAgentAccess.generated.js";

/** A key that changes exactly when the selected user or organisation does. */
export function useAgentIdentityKey(): string {
  const { currentUser } = useUserData();
  const { selectedUserId } = useSelectedUser();
  const organisationId = currentUser?.organisation.id ?? "";
  return `${selectedUserId ?? ""}:${organisationId}`;
}

export type AgentAccess = {
  enabled: boolean;
  /** Whether the backend can speak (a Gemini key is configured) — the speaker switch is hidden entirely when false. */
  speech: boolean;
};

const NO_ACCESS: AgentAccess = { enabled: false, speech: false };

export function useAgentAccess(): AgentAccess {
  const apolloClient = useApolloClient();
  const identityKey = useAgentIdentityKey();
  const { hasCapability } = useUserData();
  const hasAiAssistant = hasCapability("aiAssistant");
  const [access, setAccess] = React.useState<AgentAccess>(NO_ACCESS);

  React.useEffect(() => {
    let cancelled = false;
    setAccess(NO_ACCESS);
    apolloClient
      .query<AgentAccessQuery>({
        query: AgentAccessDocument,
        fetchPolicy: "network-only",
        // Defence in depth: the backend never throws for a plain gate
        // refusal (it answers `{ enabled: false }`), but this still opts
        // the probe out of the global Apollo error link's toast
        // (`ApolloClientProvider.tsx`) for any other error this query could
        // surface — a refused/ungated user must never see an error toast
        // right after login.
        context: { noErrorNotification: true },
      })
      .then(({ data }) => {
        if (cancelled) return;
        const result = getDataOrNull(data.agentAccess);
        setAccess({
          enabled: result?.enabled === true,
          speech: result?.speech === true,
        });
      })
      .catch(() => {
        if (!cancelled) setAccess(NO_ACCESS);
      });
    return () => {
      cancelled = true;
    };
    // Only the identity and the organisation's `aiAssistant` capability (it
    // can be toggled within a session) decide when to re-probe;
    // `apolloClient` is recreated with the same identity and would otherwise
    // re-run this on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityKey, hasAiAssistant]);

  return access;
}
